import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { getTimeZones } from "@vvo/tzdb";
import "./index.scss";
import { ReactComponent as Package } from "../../assets/track/package.svg";
import { ReactComponent as Arrows } from "../../assets/track/chevrons-right.svg";
import { ReactComponent as Number } from "../../assets/track/Group 99.svg";
import { ReactComponent as Close } from "../../assets/track/close.svg";
import { ReactComponent as Share } from "../../assets/track/link.svg";
import classNames from "classnames";
import {
  getDay,
  getShortDate,
  getStart,
  getTime,
} from "../../hooks/dateOutputs";
import { useParams } from "react-router";
import ContentState from "../../stores/ContentState";
import GlobalState from "../../stores/GlobalState";
import { CAPTCHA } from "../../mocks/doman";
import { encode } from "punycode";
import ErrorBlock from "../ErrorBlock";
import SeoBlock from "../SeoBlock";

type TimezoneItem = ReturnType<typeof getTimeZones>[number];

export const ReCAPTCHAv3Utils = (
  action: string,
  onSuccess: any,
  onError: any
) => {
  if ((window as any).grecaptcha) {
    (window as any).grecaptcha.ready(function () {
      var config = {
        action: action,
      };
      try {
        var query = (window as any).grecaptcha.execute(CAPTCHA, config);
        if (onSuccess) {
          query.then(onSuccess);
        }
      } catch (e) {
        var message = (e && (e as any).message) || "Captcha request error.";
        if (onError) {
          onError(message);
        }
      }
    });
  } else {
    if (onError) {
      onError("reCAPTCHA v3 is not loaded correctly.");
    }
  }
};

const isExcludedTimezone = (timezone: TimezoneItem) => {
  const name = timezone.name?.toLowerCase() || "";
  const alternativeName = timezone.alternativeName?.toLowerCase() || "";
  const countryName = timezone.countryName?.toLowerCase() || "";
  const countryCode = timezone.countryCode?.toUpperCase() || "";
  const cities = (timezone.mainCities || []).join(" ").toLowerCase();
  const group = (timezone.group || []).join(" ").toLowerCase();

  return (
    countryCode === "RU" ||
    countryName === "russia" ||
    countryName === "russian federation" ||
    name.startsWith("europe/moscow") ||
    name.startsWith("europe/kaliningrad") ||
    name.startsWith("europe/samara") ||
    name.startsWith("europe/volgograd") ||
    name.startsWith("europe/saratov") ||
    name.startsWith("europe/ulyanovsk") ||
    name.startsWith("europe/kirov") ||
    name.startsWith("asia/anadyr") ||
    name.startsWith("asia/barnaul") ||
    name.startsWith("asia/chita") ||
    name.startsWith("asia/irkutsk") ||
    name.startsWith("asia/kamchatka") ||
    name.startsWith("asia/khandyga") ||
    name.startsWith("asia/krasnoyarsk") ||
    name.startsWith("asia/magadan") ||
    name.startsWith("asia/novokuznetsk") ||
    name.startsWith("asia/novosibirsk") ||
    name.startsWith("asia/omsk") ||
    name.startsWith("asia/sakhalin") ||
    name.startsWith("asia/srednekolymsk") ||
    name.startsWith("asia/tomsk") ||
    name.startsWith("asia/ust-nera") ||
    name.startsWith("asia/vladivostok") ||
    name.startsWith("asia/yakutsk") ||
    name.startsWith("asia/yekaterinburg") ||
    alternativeName.includes("moscow time") ||
    cities.includes("moscow") ||
    cities.includes("saint petersburg") ||
    group.includes("europe/moscow")
  );
};

const TrackingContent = observer(() => {
  //const { id: id } = useParams<any>()
  const params = useParams();

  const [looseConnect, setLoose] = useState(false);
  const [isDisabled, setDisabled] = useState(false);
  const errorTrackingCodes = [
    "521-1",
    "405",
    "540",
    "541",
    "551",
    "511",
    "552",
    "514",
    "33",
    "532",
    "553",
    "34",
    "31",
    "714",
    "613",
    "521-2",
    "521-3",
    "521-4",
    "521-5",
    "518",
    "545",
    "562",
    "563",
    "787",
  ];

  const [history, setHistory] = useState<any>([]);

  const [data, setData] = useState<any>(0);
  const [state, setState] = useState("");
  const [copy, setCopy] = useState(false);

  const [timezoneMode, setTimezoneMode] = useState("kyiv");
  const [customTimezone, setCustomTimezone] = useState("Europe/Kyiv");
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState("");
  const [timezones, setTimezones] = useState<TimezoneItem[]>([]);

  const track = async (number: string) => {
    if (!number.length) return;

    var onSuccess = function (token: any) {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("num", number.toString());
      fd.append("lang", GlobalState.currentLocale.lang);
      fetch(`https://personal.novaposhtaglobal.ua/tracking.php`, { //np.romura.space https://personal.novaposhtaglobal.ua/tracking.php
        method: "POST",
        body: fd,
      })
        .then((response) => response.json())
        .then(
          (result) => {
            if (!result) {
              setLoose(true);
            }
            if (result == 203) {
              setData(null);
              setDisabled(false);
              return;
            }
            if (localStorage.getItem("trackingHistory")) {
              const dt = JSON.parse(localStorage.getItem("trackingHistory")!);
              dt.push({
                date: result.date,
                numberBill: result.billNumber,
              });
              localStorage.setItem("trackingHistory", JSON.stringify(dt));
              setHistory(dt);
            } else {
              localStorage.setItem(
                "trackingHistory",
                JSON.stringify([
                  {
                    date: result.date,
                    numberBill: result.billNumber,
                  },
                ])
              );
              setHistory([
                {
                  date: result.date,
                  numberBill: result.billNumber,
                },
              ]);
            }

            setData(result);
            setDisabled(false);
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push({ event: "click_track" });
          },
          (error) => {
            setLoose(true);
            return;
          }
        );
    };
    var onError = function (message: any) {
      alert("Error: " + message);
    };
    ReCAPTCHAv3Utils("track", onSuccess, onError);
  };

  const share = () => {
    if (
      document
        .querySelector(".tracking .button.grey-border")
        ?.classList.contains("button-active")
    ) {
      setTimeout(() => {
        document
          .querySelector(".tracking .button.grey-border")
          ?.classList.toggle("button-active");
      }, 500);
    } else {
      document
        .querySelector(".tracking .button.grey-border")
        ?.classList.toggle("button-active");
      setTimeout(() => {
        document
          .querySelector(".tracking .button.grey-border")
          ?.classList.toggle("button-active");
      }, 500);
    }

    const { lang }: any = params;
    navigator.clipboard
      .writeText(
        `${window.location.origin}${
          lang ? "/" + lang : ""
        }/track/?Tracking_ID=${state}`
      )
      .then(() => setCopy(true));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("Tracking_ID")) return;

    setState(params.get("Tracking_ID")!);
    track(params.get("Tracking_ID")!);
  }, []);

  useEffect(() => {
    if (copy) {
      setTimeout(() => {
        setCopy(false);
      }, 3000);
    }
  }, [copy]);

  const clearHistory = () => {
    localStorage.removeItem("trackingHistory");
    setHistory([]);
  };

  const deleteItem = (id: number) => {
    setHistory(history.filter((h: any, i: number) => i != id));
    const items = document.querySelectorAll(".tracking__history-item");
    const close = items[id].querySelector(".tracking__close");
    if (close?.classList.contains("close-active")) {
      setTimeout(() => {
        close?.classList.toggle("close-active");
      }, 500);
    } else {
      close?.classList.toggle("close-active");
      setTimeout(() => {
        close?.classList.toggle("close-active");
      }, 500);
    }
    localStorage.setItem(
      "trackingHistory",
      JSON.stringify(history.filter((h: any, i: number) => i != id))
    );
  };

  useEffect(() => {
    if (localStorage.getItem("trackingHistory")) {
      const dt = JSON.parse(localStorage.getItem("trackingHistory")!);
      setHistory(dt);
    }
  }, []);

  const dates =
    (history.length &&
      new Set(
        history?.map((item: any) => {
          return new Date(getStart(item.date)).getTime();
        })
      )) ||
    [];

  const getItems = (date: number) => {
    if (!history.length) return [];
    let items = history?.filter((item: any) => {
      if (new Date(new Date(item.date).toDateString()).getTime() == date)
        return item;
    });

    items = items.sort((a: any, b: any) => a.id - b.id);

    return items;
  };

  const getReplaceStatus = (status: string) => {
    let string: any = status.split("");

    string = string.map((s: any) => {
      if (s.includes(".")) {
        return ". ";
      } else return s;
    });

    return string.join("");
  };

  useEffect(() => {
    const timezoneList = getTimeZones({ includeUtc: true }).filter(
      (timezone) => !isExcludedTimezone(timezone)
    );

    setTimezones(timezoneList);

    const saved = localStorage.getItem("trackingTimezone");
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const savedMode = parsed.mode || "kyiv";
      const savedTimezone = parsed.timezone || "Europe/Kyiv";
      const savedTimezoneData = timezoneList.find(
        (timezone) => timezone.name === savedTimezone
      );

      if (savedMode === "custom" && !savedTimezoneData) {
        setTimezoneMode("kyiv");
        setCustomTimezone("Europe/Kyiv");
        localStorage.setItem(
          "trackingTimezone",
          JSON.stringify({ mode: "kyiv", timezone: "Europe/Kyiv" })
        );
        return;
      }

      setTimezoneMode(savedMode);
      if (savedMode === "custom") {
        setCustomTimezone(savedTimezone);
      }
    } catch (error) {
      console.warn("Invalid saved tracking timezone", error);
    }
  }, []);

  const getBrowserTimezone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Kyiv";

  const getActiveTimezone = () => {
    if (timezoneMode === "browser") return getBrowserTimezone();
    if (timezoneMode === "utc") return "UTC";
    if (timezoneMode === "custom") return customTimezone;
    return "Europe/Kyiv";
  };

  const saveTimezone = (mode: string, timezone?: string) => {
    const timezoneToSave =
      mode === "browser"
        ? getBrowserTimezone()
        : mode === "utc"
        ? "UTC"
        : mode === "custom"
        ? timezone || customTimezone
        : "Europe/Kyiv";

    setTimezoneMode(mode);
    if (mode === "custom" && timezone) {
      setCustomTimezone(timezone);
    }

    localStorage.setItem(
      "trackingTimezone",
      JSON.stringify({ mode, timezone: timezoneToSave })
    );

    setTimezoneSearch("");
    setTimezoneOpen(false);
  };

  const getTimezoneOffset = (date: Date, timeZone: string) => {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName: "shortOffset",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(date);

      const tzName = parts.find(
        (part) => part.type === "timeZoneName"
      )?.value;

      if (!tzName || tzName === "GMT") return "UTC+00:00";
      return tzName.replace("GMT", "UTC");
    } catch {
      return "";
    }
  };

  const formatOffsetFromMinutes = (minutes: number) => {
    const sign = minutes >= 0 ? "+" : "-";
    const absoluteMinutes = Math.abs(minutes);
    const hours = Math.floor(absoluteMinutes / 60);
    const mins = absoluteMinutes % 60;

    return `UTC${sign}${String(hours).padStart(2, "0")}:${String(
      mins
    ).padStart(2, "0")}`;
  };

  const normalizeTimezoneSearch = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/^gmt/, "utc");

  const formatTrackingDate = (timestamp?: number | string) => {
    const numericTimestamp = parseInt(String(timestamp), 10);
    if (!numericTimestamp) return "";

    const date = new Date(numericTimestamp * 1000);

    return new Intl.DateTimeFormat("en-GB", {
      timeZone: getActiveTimezone(),
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .format(date)
      .replace(/\//g, ".");
  };

  const getTimezoneTitle = () => {
    if (timezoneMode === "browser") return "Local Time";
    if (timezoneMode === "utc") return "UTC / GMT";
    if (timezoneMode === "custom") return customTimezone.replace(/_/g, " ");
    return "Kyiv Time";
  };

  const filteredTimezones = timezones.filter((timezone) => {
    const search = normalizeTimezoneSearch(timezoneSearch);
    const compactSearch = search.replace(/\s/g, "");
    if (!search) return true;

    const offsetFull = formatOffsetFromMinutes(
      timezone.currentTimeOffsetInMinutes
    ).toLowerCase();
    const offsetShort = offsetFull
      .replace(":00", "")
      .replace(/^utc\+0/, "utc+")
      .replace(/^utc-0/, "utc-");

    const searchableText = [
      timezone.name,
      timezone.alternativeName,
      timezone.countryName,
      timezone.countryCode,
      timezone.continentName,
      timezone.abbreviation,
      ...(timezone.mainCities || []),
      ...(timezone.group || []),
      offsetFull,
      offsetShort,
      offsetFull.replace("utc", "gmt"),
      offsetShort.replace("utc", "gmt"),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      searchableText.includes(search) ||
      searchableText.replace(/\s/g, "").includes(compactSearch)
    );
  });

  const representativeTimezones = Array.from(
    timezones
      .filter(({ currentTimeOffsetInMinutes: offset }) =>
        typeof offset === "number" && offset >= -11 * 60 && offset <= 14 * 60
      )
      .sort(
        (a, b) =>
          a.currentTimeOffsetInMinutes - b.currentTimeOffsetInMinutes
      )
      .reduce<Map<number, TimezoneItem>>((map, timezone) => {
        if (!map.has(timezone.currentTimeOffsetInMinutes)) {
          map.set(timezone.currentTimeOffsetInMinutes, timezone);
        }
        return map;
      }, new Map())
      .values()
  );

  const displayedTimezones = timezoneSearch.trim()
    ? filteredTimezones
    : representativeTimezones;

  if (!ContentState.tracking) return <></>;

  const content: any = ContentState.tracking;

  if (looseConnect) {
    return <ErrorBlock />;
  }

  let trackDt = [];

  if (data) {
    trackDt = JSON.parse(JSON.stringify(data.historyStatus));
  }

  return (
    <section
      className="tracking"
      style={content.bg ? { backgroundImage: `url(${content.bg})` } : {}}
    >
      <div className="tracking__container">
        <div className="tracking__row">
          <div className={classNames("tracking__form", data && "fill")}>
            <div
              className="tracking__title"
              dangerouslySetInnerHTML={{
                __html:
                  window.innerWidth > 768 ? content.title : content.mobTitle,
              }}
            ></div>
            <div className="tracking__form-row">
              <div
                className={classNames(
                  "tracking__input",
                  data == null && "error"
                )}
              >
                <form
                  style={{ width: "100%", display: "flex" }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    track(state);
                  }}
                >
                  <input
                    maxLength={40}
                    value={state}
                    className="input"
                    type={"text"}
                    onChange={(e) => {
                      if (e.target.value.includes(" ")) {
                        return;
                      } else setState(e.target.value);
                    }}
                  />
                </form>
                <Close
                  onClick={() => {
                    const close = document.querySelector(
                      ".tracking__input .tracking__close"
                    );

                    if (close?.classList.contains("close-active")) {
                      setTimeout(() => {
                        close?.classList.toggle("close-active");
                      }, 500);
                    } else {
                      close?.classList.toggle("close-active");
                      setTimeout(() => {
                        close?.classList.toggle("close-active");
                      }, 500);
                    }
                    setState("");
                  }}
                  className="tracking__close close-active"
                />
              </div>
              <div
                className={classNames(
                  "button black",
                  isDisabled && "button-disabled"
                )}
                onClick={() => {
                  if (state.length) {
                    setDisabled(true);
                    track(state);
                  }
                }}
              >
                {content.buttonTitle}
              </div>
            </div>
          </div>
          {!data && data != null && (
            <div className="tracking__imgs">
              {ContentState.track?.banners?.map((i: any, id: number) => (
                <a href={i.link} target="__blank" key={id}>
                  <img src={i.src} key={id} />
                </a>
              ))}
            </div>
          )}
          {data == null ? (
            <div className="tracking__error">
              <img className="tracking__error-icon" src={content.error.icon} />
              <div
                className="tracking__error-text"
                dangerouslySetInnerHTML={{ __html: content.error.text }}
              ></div>
            </div>
          ) : (
            <></>
          )}
          {data ? (
            <div className="tracking__bill">
              <div className="tracking__bill-title ">
                {content.bill.title} {data.billNumber}
              </div>
              <div
                className={classNames(
                  "tracking__bill-row tracking__bill-text",
                  errorTrackingCodes.includes(trackDt[0].code) && "error",
                  trackDt[0].code == "3-1" && "recieved"
                )}
              >
                <Package
                  className={classNames(
                    "tracking__bill-icon",
                    errorTrackingCodes.includes(trackDt[0].code) && "error",
                    trackDt[0].code == "3-1" && "recieved"
                  )}
                />{" "}
                <div className="tracking__bill-col">
                  {trackDt[0].status}
                  <span className="tracking__bill-separator"></span>
                  <span className="tracking__bill-country">
                    {data.location}
                  </span>
                  <span className="tracking__bill-separator"></span>
                  {formatTrackingDate(data.timestamp)}
                </div>
              </div>
              <div
                className={classNames("tracking__bill-row tracking__bill-text")}
              >
                <Arrows className="tracking__bill-arrows" />{" "}
                <div className="tracking__bill-route">
                  <TrackingFlag name={data.from} byName={false} /> {data.from}
                </div>{" "}
                <span className="tracking__bill-separator bill-route"></span>
                <div className="tracking__bill-route">
                  <TrackingFlag name={data.to} byName={false} /> {data.to}
                </div>
              </div>
              <div className="tracking__bill-row tracking__bill-text">
                <Number className="tracking__bill-icon" /> {data.bills}
              </div>

              <div className="tracking__bill-bottom">
                <div
                  className="button white grey-border button-active"
                  onClick={share}
                >
                  <Share /> {content.bill.share}
                </div>

                <div className="tracking__timezone">
                  <button
                    type="button"
                    className="tracking__timezone-current"
                    onClick={() => setTimezoneOpen((open) => !open)}
                  >
                    <span className="tracking__timezone-icon">🌐</span>
                    <span>
                      <b>Display time zone</b>
                      <small>
                        {getTimezoneTitle()},{" "}
                        {getTimezoneOffset(new Date(), getActiveTimezone())}
                      </small>
                    </span>
                    <span
                      className={classNames(
                        "tracking__timezone-arrow",
                        timezoneOpen && "tracking__timezone-arrow--open"
                      )}
                    >
                      <svg width="12" height="8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m1 1.5 5 5 5-5" stroke="#33332F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                    </span>
                  </button>

                  {timezoneOpen && (
                    <div className="tracking__timezone-dropdown">
                      <input
                        className="tracking__timezone-search"
                        placeholder="Search by city, country or UTC offset"
                        value={timezoneSearch}
                        onChange={(event) =>
                          setTimezoneSearch(event.target.value)
                        }
                      />

                      <div className="tracking__timezone-label">Recommended</div>

                      <button
                        type="button"
                        onClick={() => saveTimezone("browser")}
                        className={timezoneMode === "browser" ? "active" : ""}
                      >
                        <span className="tracking__timezone-option-content">
                          <strong>Local Time</strong>
                          <small>User browser time zone</small>
                        </span>
                        {timezoneMode === "browser" && (
                          <span className="tracking__timezone-check">✓</span>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => saveTimezone("utc")}
                        className={timezoneMode === "utc" ? "active" : ""}
                      >
                        <span className="tracking__timezone-option-content">
                          <strong>UTC / GMT</strong>
                          <small>Coordinated Universal Time</small>
                        </span>
                        {timezoneMode === "utc" && (
                          <span className="tracking__timezone-check">✓</span>
                        )}
                      </button>

                      <div className="tracking__timezone-label">
                        Other time zones
                      </div>

                      <div className="tracking__timezone-results">
                        {displayedTimezones.length ? (
                          displayedTimezones.map((timezone) => {
                            const offset = formatOffsetFromMinutes(
                              timezone.currentTimeOffsetInMinutes
                            );
                            const cities = timezoneSearch.trim()
                              ? timezone.mainCities?.slice(0, 4).join(", ")
                              : timezone.mainCities?.[0];
                            const isActive =
                              timezoneMode === "custom" &&
                              customTimezone === timezone.name;

                            return (
                              <button
                                key={timezone.name}
                                type="button"
                                onClick={() =>
                                  saveTimezone("custom", timezone.name)
                                }
                                className={isActive ? "active" : ""}
                              >
                                <span className="tracking__timezone-option-content">
                                  <strong>
                                    {timezoneSearch.trim()
                                      ? `${timezone.alternativeName || timezone.name} (${offset})`
                                      : `${offset} — ${
                                          timezone.alternativeName || timezone.name
                                        }`}
                                  </strong>
                                  <small>
                                    {[cities, timezone.countryName]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </small>
                                </span>
                                {isActive && (
                                  <span className="tracking__timezone-check">✓</span>
                                )}
                              </button>
                            );
                          })
                        ) : (
                          <div className="tracking__timezone-empty">
                            No time zones found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className={classNames(
                    "tracking__bill-copy",
                    copy && "visible"
                  )}
                >
                  {content.bill.copy}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
        {data ? (
          <div className="tracking__row">
            {data ? (
              <div className="tracking__statuses">
                <div className="tracking__statuses-head">
                  <div className="tracking__statuses-title">
                    {content.statuses.title}
                  </div>
                  <div className="tracking__statuses-timezone-note">
                    Event times are shown in the selected time zone
                    <span>
                      {getTimezoneOffset(new Date(), getActiveTimezone())}
                    </span>
                  </div>
                </div>
                <div className="tracking__statuses-list">
                  {data.historyStatus?.map((hi: any, id: number) => (
                    <div
                      className={classNames(
                        "tracking__statuses-item",
                        id + 1 == data.historyStatus?.length && "start",
                        !id && "start",
                        errorTrackingCodes.includes(hi.code) && !id && "error",
                        hi.code == "3-1" && !id && "recieved"
                      )}
                      key={id}
                    >
                      <div className="tracking__bill-text">
                        {getReplaceStatus(hi.status)}
                      </div>
                      <div className="tracking__statuses-row">
                        {formatTrackingDate(hi.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        ) : (
          <></>
        )}
        {history?.length ? (
          <div className="tracking__row">
            <div className="tracking__history">
              <div className="tracking__history-top">
                <div className="tracking__history-title">
                  {content.history.title}
                </div>
                <div className="tracking__history-clear" onClick={clearHistory}>
                  {window.innerWidth > 480
                    ? content.history.clear
                    : content.history.clearMob}
                </div>
              </div>
              {Array.from(dates).map((d: any, i) => (
                <div className="tracking__history-block" key={i}>
                  <div className="tracking__history-sort">
                    {getDay(d, content)},{" "}
                    {getShortDate(d, content, GlobalState.currentLocale.lang)}
                  </div>

                  {getItems(d).map((item: any, index: number) => (
                    <div className="tracking__history-item" key={index}>
                      <div className="tracking__history-time">
                        {getTime(item.date)}
                      </div>
                      <div className="tracking__history-number">
                        {item.numberBill}
                      </div>
                      <Close
                        onClick={() => deleteItem(index)}
                        className="tracking__close close-active"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      {ContentState.track?.seo && ContentState.track?.seo.title?.length > 0 && (
        <SeoBlock isLight seo={ContentState.track?.seo} />
      )}
    </section>
  );
});

export default TrackingContent;

export const TrackingFlag = observer(
  ({ name, byName }: { name: string; byName: boolean }) => {
    const [img, setImg] = useState("");
    useEffect(() => {
      if (byName) {
        setImg(`https://flagsapi.com/${name}/flat/64.png`);
        return;
      }
      setImg(`https://flagsapi.com/${name}/flat/64.png`);
    }, [name]);
    return <img src={img} />;
  }
);